import $ from '../core/Dom';

/**
 * Before / after image compare.
 *
 * Adapted from the AI Image Editor site's hero. The markup ships with the
 * handle centred and the "after" layer already clipped, so with no JS it
 * still reads as a split before/after image — this upgrades it into a
 * draggable, keyboard-operable slider.
 *
 * Everything positional (the clip, the divider, the handle) reads from one
 * custom property on the root, so a drag is a single style write rather than
 * three.
 */
export default el => {
    const $el = $(el);

    let handle = null;
    let $handle = null;

    let pos = 50; // percent revealed, matching the markup default
    let dragging = false;

    const apply = next => {
        pos = Math.min(100, Math.max(0, next));
        el.style.setProperty('--ds-compare-pos', `${pos}%`);

        if (handle) {
            handle.setAttribute('aria-valuenow', String(Math.round(pos)));
        }
    };

    const percentFromX = clientX => {
        const rect = el.getBoundingClientRect();
        return ((clientX - rect.left) / rect.width) * 100;
    };

    // Dragging works anywhere in the image, not just on the knob
    const onPointerDown = e => {
        dragging = true;
        el.setPointerCapture(e.pointerId);
        apply(percentFromX(e.clientX));

        // Suppresses text selection and the browser's native image drag.
        // Deliberately no focus() — that would leave a focus ring sitting on
        // the handle after every mouse drag. Keyboard users tab to it.
        e.preventDefault();
    };

    const onPointerMove = e => {
        if (dragging) {
            apply(percentFromX(e.clientX));
        }
    };

    const onPointerUp = e => {
        if (!dragging) {
            return;
        }

        dragging = false;

        if (el.hasPointerCapture(e.pointerId)) {
            el.releasePointerCapture(e.pointerId);
        }
    };

    const onKeyDown = e => {
        const step = e.shiftKey ? 10 : 2;

        switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowDown':
                apply(pos - step);
                break;
            case 'ArrowRight':
            case 'ArrowUp':
                apply(pos + step);
                break;
            case 'Home':
                apply(0);
                break;
            case 'End':
                apply(100);
                break;
            default:
                return;
        }

        e.preventDefault();
    };

    const init = () => {
        handle = $el.find('[data-compare-handle]').get(0);

        if (!handle) {
            return;
        }

        $handle = $(handle);

        // The knob only becomes a real control once the drag behind it exists
        handle.setAttribute('role', 'slider');
        handle.setAttribute('tabindex', '0');
        handle.setAttribute('aria-label', 'Compare the original and edited image');
        handle.setAttribute('aria-orientation', 'horizontal');
        handle.setAttribute('aria-valuemin', '0');
        handle.setAttribute('aria-valuemax', '100');

        $el.addClass('is-interactive');
        apply(pos);

        $el.on('pointerdown', onPointerDown);
        $el.on('pointermove', onPointerMove);
        $el.on('pointerup', onPointerUp);
        $el.on('pointercancel', onPointerUp);
        $handle.on('keydown', onKeyDown);
    };

    const destroy = () => {
        $el.off('pointerdown');
        $el.off('pointermove');
        $el.off('pointerup');
        $el.off('pointercancel');

        if ($handle) {
            $handle.off('keydown');
        }

        $el.removeClass('is-interactive');
        handle = null;
        $handle = null;
    };

    return {
        init,
        destroy
    };
};
